import React, { useEffect } from 'react';
import {
	ApplicationStatus,
	BaseAPIResponse,
	CreateApplicationInput,
	PositionDocument,
	PositionStatus,
	UIApplicationCardState,
	UIExtendedApplication,
} from 'types';
import { get } from 'lodash';
import ApplicationCardHeader from './ApplicationCardHeader';
import PositionDetails from './PositionDetails';
import ApplicationCardFooter from './ApplicationCardFooter';
import CurrentApplicationStatus from './CurrentApplicationStatus';
import { UseMutationResult, useMutation, useQueryClient } from 'react-query';
import { createApplication, createApplicationV2 } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { Badge } from 'flowbite-react';
import { Tooltip } from '@mui/material';
interface Props {
	setIsNotInterestedModalOpen: (arg: boolean) => void;
	setIsCancelInterviewModalOpen: (arg: boolean) => void;
	setIsLinkedInModalOpen: (arg: boolean) => void;
	setIsLinkedInResumeModalOpen: (arg: boolean) => void;
	application: UIExtendedApplication;
	onCardClick: (
		arg: any,
		flag: boolean,
		applicationStatusType: ApplicationStatus,
		application: UIExtendedApplication,
	) => void; // ApplicationDrawerDetail
	setCurrentlySelectedApplication: (arg: UIExtendedApplication) => void;
	optOutOfApplicationProcessLoading: boolean;
	createApplicationLoading: boolean;
	createApplicationMutation: UseMutationResult<BaseAPIResponse, Error, CreateApplicationInput>;
	selectedTab: string;
}
const ApplicationCardV2 = ({
	setIsNotInterestedModalOpen,
	setIsCancelInterviewModalOpen,
	setIsLinkedInModalOpen,
	setIsLinkedInResumeModalOpen,
	onCardClick,
	application,
	setCurrentlySelectedApplication,
	optOutOfApplicationProcessLoading,
	createApplicationLoading,
	createApplicationMutation,
	selectedTab,
}: Props) => {
	const [applicationCardState, setApplicationCardState] = React.useState<UIApplicationCardState>(
		UIApplicationCardState.MATCHED_CARD,
	);
	const applicationStatus = application.status as ApplicationStatus;
	const isClosedApplicationsTab = selectedTab === 'closedApplications';
	useEffect(() => {
		if (isClosedApplicationsTab) {
			if ([ApplicationStatus.REJECTED, ApplicationStatus.AUTO_REJECTED].includes(applicationStatus)) {
				setApplicationCardState(UIApplicationCardState.REJECTED_CARD);
			} else if (applicationStatus === ApplicationStatus.JOBSEEKER_OPTED_OUT) {
				setApplicationCardState(UIApplicationCardState.JOBSEEKER_OPTED_OUT_CARD);
			} else if (
				[
					ApplicationStatus.PLACED,
					ApplicationStatus.VERBALLY_PLACED,
					ApplicationStatus.PLACED_ELSEWHERE,
				].includes(applicationStatus)
			) {
				setApplicationCardState(UIApplicationCardState.PLACED_CARD);
			} else {
				setApplicationCardState(UIApplicationCardState.CLOSED_CARD);
			}
			return;
		}

		if (!application.status && !application.isPosition) {
			return;
		}
		if (
			application.isPosition ||
			(application.status &&
				[ApplicationStatus.OPENED_BY_EMPLOYER, ApplicationStatus.OPENED_BY_ADMIN].includes(application.status))
		) {
			setApplicationCardState(UIApplicationCardState.MATCHED_CARD);
		} else if (application.status === ApplicationStatus.SHORTLISTED) {
			setApplicationCardState(UIApplicationCardState.SHORTLISTED_CARD);
		} else if (application.status && [ApplicationStatus.WAITING_FOR_RECRUITER_ACTION].includes(application.status)) {
			setApplicationCardState(UIApplicationCardState.WAITING_FOR_RECRUITER_ACTION_CARD);
		} else if (
			application.status &&
			[ApplicationStatus.PENDING_FOR_ASSIGNMENT_SUBMISSION_APPROVAL].includes(application.status)
		) {
			setApplicationCardState(UIApplicationCardState.PENDING_FOR_ASSIGNMENT_SUBMISSION_APPROVAL_CARD);
		} else if (
			application.status &&
			[ApplicationStatus.PENDING_FOR_OFFER_LETTER_APPROVAL].includes(application.status)
		) {
			setApplicationCardState(UIApplicationCardState.PENDING_FOR_OFFER_LETTER_APPROVAL_CARD);
		} else if (application.status && [ApplicationStatus.QUALIFIED_ALL_ROUNDS].includes(application.status)) {
			setApplicationCardState(UIApplicationCardState.QUALIFIED_ALL_ROUNDS_CARD);
		} else if (application.status === ApplicationStatus.INTERVIEW_SCHEDULED) {
			setApplicationCardState(UIApplicationCardState.INTERVIEW_ROUND_IN_PROGRESS_CARD);
		} else if (application.status === ApplicationStatus.ASSIGNMENT_IN_PROGRESS) {
			setApplicationCardState(UIApplicationCardState.ASSIGNMENT_ROUND_IN_PROGRESS_CARD);
		} else if (application.status === ApplicationStatus.JOBSEEKER_OPTED_OUT) {
			setApplicationCardState(UIApplicationCardState.JOBSEEKER_OPTED_OUT_CARD);
		} else if (application.status === ApplicationStatus.OFFER_ROLLED_OUT) {
			setApplicationCardState(UIApplicationCardState.OFFERED_CARD);
		} else if (application.status === ApplicationStatus.PENDING_FOR_OPT_OUT_APPROVAL) {
			setApplicationCardState(UIApplicationCardState.PENDING_FOR_OPT_OUT_APPROVAL_CARD);
		} else if (application.status === ApplicationStatus.VERBALLY_PLACED) {
			setApplicationCardState(UIApplicationCardState.PLACED_CARD);
		} else if (application.status === ApplicationStatus.PLACED) {
			setApplicationCardState(UIApplicationCardState.PLACED_CARD);
		} else if (application.status === ApplicationStatus.CHANGED_OFFER_TERMS) {
			setApplicationCardState(UIApplicationCardState.CHANGED_OFFER_TERMS_CARD);
		} else if (
			application.status &&
			[
				ApplicationStatus.CLOSED_BY_ADMIN,
				ApplicationStatus.CLOSED_BY_RECRUITER,
				ApplicationStatus.EXPIRED,
				ApplicationStatus.OFFER_REVOKED,
				ApplicationStatus.PLACED_ELSEWHERE,
				ApplicationStatus.OFFER_REJECTED,
			].includes(application.status)
		) {
			setApplicationCardState(UIApplicationCardState.CLOSED_CARD);
		} else if (application.status && [ApplicationStatus.REJECTED, ApplicationStatus.AUTO_REJECTED].includes(application.status)) {
			setApplicationCardState(UIApplicationCardState.REJECTED_CARD);
		} else {
			setApplicationCardState(UIApplicationCardState.APPLIED_CARD);
		}
	}, [application.status, application.isPosition, selectedTab, isClosedApplicationsTab, applicationStatus]);

	// Don't hide footer for Applied tab - always show Create Ticket + side arrow
	const hideFooter =
		selectedTab !== 'appliedApplications' &&
		([
			UIApplicationCardState.SHORTLISTED_CARD,
			UIApplicationCardState.CLOSED_CARD,
			UIApplicationCardState.JOBSEEKER_OPTED_OUT_CARD,
			UIApplicationCardState.WAITING_FOR_RECRUITER_ACTION_CARD,
			UIApplicationCardState.REJECTED_CARD,
		].includes(applicationCardState) ||
			([UIApplicationCardState.PENDING_FOR_OPT_OUT_APPROVAL_CARD].includes(applicationCardState) &&
				!application.history?.some(history => history.newStatus === ApplicationStatus.OFFER_ROLLED_OUT)) ||
			application.pendingForAdminApproval ||
			application.adminRejectedOfferLetter);

	const position = application.position ?? {} as PositionDocument;
	const locationArray = [
		...(application.position?.workLocations ?? []),
		...(application.position?.workLocation ? [application.position.workLocation] : []),
	];
	const locationString = locationArray.join(' | ');
	const handleCardClick = () => {
		onCardClick(
			{
				id: application._id,
				hasBond: position.hasBond,
				bondDetails: position.bondDetails,
				workingMode: position.workingMode,
				workingExp: position.positionRequirement.workExperience
					? `${Math.floor(position.positionRequirement.workExperience.min)} Yrs ${Math.ceil(position.positionRequirement.workExperience?.min * 12) % 12
					} Mon - ${Math.floor(position.positionRequirement.workExperience.max)} Yrs ${Math.ceil(position.positionRequirement.workExperience?.max * 12) % 12
					} Mon `
					: '',
				minSalary: position.salary.min,
				maxSalary: position.salary.max,
				location: locationString,
				jobType: position.positionType,
				description: position.description,
				companyLogo: position.company.logo,
				companyUrl: position.company.url,
				companyName: position.company.name,
				companyPosition: position.title,
				isLoadingApplying: false,
				isPositionClosed: position.positionStatus === PositionStatus.CLOSED,
				minInternshipSalary: position?.internshipSalary?.min,
				maxInternshipSalary: position?.internshipSalary?.max,
				internshipDuration: position.internshipDuration,
				postedOn: get(position, 'createAt', ''),
				expiresOn: get(position, 'expiresAt', ''),
				updatedAt: get(position, 'updatedAt', ''),
			},
			[
				ApplicationStatus.OPENED_BY_ADMIN,
				ApplicationStatus.OPENED_BY_EMPLOYER,
				ApplicationStatus.APPLIED_BY_JOBSEEKER,
			].includes(applicationStatus) || application.isPosition,
			applicationStatus,
			application,
		);
	};

	// Compute badge text locally (fallback) to position it at card top-right
	const computeJobseekerBadge = (types?: ('auto' | 'manual' | 'you_might_be_interested')[] | null) => {
		if (!types || types.length === 0) return null;
		const hasAuto = types.includes('auto');
		const hasManual = types.includes('manual');
		const hasInterestedProfile = types.includes('you_might_be_interested');
		if (hasInterestedProfile) return 'You might be interested';
		if (hasAuto && !hasManual) return null; // auto only => no badge
		if (hasManual && !hasAuto) return 'Recommended';
		if (hasAuto && hasManual) return 'Recommended';
		return null;
	};
	const badgeText = (application as any)?.badgeTextForJobseeker ?? computeJobseekerBadge(application?.recommendationTypes);
	const badgeTooltip =
		badgeText === 'You might be interested' ? 'You might be interested in this job' : badgeText;

	return (
		<div onClick={handleCardClick} className="relative rounded-[12px] border dark:border-[#3F3F3F]  max-w-[760px]">
			{/* Badge positioned at top-right corner, overlapping card border (same as account manager view) */}
			{badgeText ? (
				<Tooltip title={badgeTooltip} placement="bottom">
					<div className="absolute top-[-11px] right-[10px]">
						<Badge className="text-[#42389D] bg-[#E5EDFF] rounded-[6px]">{badgeText}</Badge>
					</div>
				</Tooltip>
			) : null}
			<div
				className={` grid md:flex justify-between px-[12px] py-[16px] dark:bg-[#1F2A37] bg-white rounded-tl-[12px] rounded-tr-[12px] ${hideFooter && 'rounded-bl-[12px] rounded-br-[12px]'
					}`}
			>
				<div className="min-w-0 flex-1 pr-2">
					<ApplicationCardHeader application={application} />
					<PositionDetails application={application} />
				</div>
				<div className="shrink-0">
					<CurrentApplicationStatus applicationCardState={applicationCardState} application={application} />
				</div>
			</div>
			{hideFooter ? null : (
				<ApplicationCardFooter
					setIsNotInterestedModalOpen={setIsNotInterestedModalOpen}
					setCurrentlySelectedApplication={setCurrentlySelectedApplication}
					setIsCancelInterviewModalOpen={setIsCancelInterviewModalOpen}
					setIsLinkedInResumeModalOpen={setIsLinkedInResumeModalOpen}
					application={application}
					applicationCardState={applicationCardState}
					createApplicationMutation={createApplicationMutation}
					createApplicationLoading={createApplicationLoading}
					optOutOfApplicationProcessLoading={optOutOfApplicationProcessLoading}
				/>
			)}
		</div>
	);
};

export default ApplicationCardV2;
